import React from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PALETTE } from './SiteLayout';
import CostOfInactionCalculator from './CostOfInactionCalculator';
import RoiCalculator from './RoiCalculator';
import { CashelBlueCompactQuote } from './CashelBlueShowcase';
import SEO from '../../components/SEO';

const SERIF = "'Playfair Display', Georgia, serif";

const PRINCIPLES = [
  {
    t: 'Sized to your operation',
    d: 'We quote on what you actually run: production volume, number of batches, products, sites, the modules and integrations you need. Not headcount alone. A three-person cheesemaker and a sixty-person factory are different businesses, and their quotes reflect that.',
  },
  {
    t: 'Licence and implementation kept apart',
    d: 'The recurring licence is what it costs to run Keystone. Implementation is a one-off, fixed-fee project to make it fit your floor, quoted separately so setup cost is never hidden inside the monthly figure.',
  },
  {
    t: 'Everything in writing before you commit',
    d: 'One call to understand the operation, then a written quote valid for 30 days. No per-user creep, no renewal surprise, no feature paywalls mid-contract. In-contract pricing is held for the term you sign.',
  },
];

const QUOTE_INPUTS = [
  'Annual production volume and number of batches',
  'Number of products, pack formats and production sites',
  'How records are kept today and where the pain actually is',
  'Modules needed now, and which can wait',
  'Integrations: accounts package, retailer EDI, label printers',
  'Implementation depth: data migration, on-site days, training',
];

const LICENCE_INCLUDES = [
  'Every core module you are licensed for, no add-on tiers inside the licence',
  'Dedicated tenant server + database isolation',
  'Daily encrypted backups + point-in-time restore (30 days)',
  'All product updates and new features at general release',
  'Security patches and endpoint bug fixes',
  'Email support · business hours · 4-hour first response',
  'Compliance Centre updates as standards evolve',
];

const SCOPED_SEPARATELY = [
  'Complex retailer EDI (Tesco / Lidl / Aldi / M&S)',
  'Historical data import beyond 12 months',
  'Bespoke reports or workflows specific to your operation',
  'Sage / QuickBooks / Xero accounts API',
  'Custom retailer label templates beyond your initial set',
];

const IMPL_INCLUDES = [
  'Discovery: we sit on your floor, map how the operation really runs',
  'Data migration from your spreadsheets / Excel / accounts package',
  'Customer code mapping (your SKUs to retailer SKUs)',
  'Branded dispatch note, pallet label and COA templates',
  'Production-data sandbox + on-site training',
  'Cut-over support: we sit with you for the first production days',
  '30-day post go-live hand-holding · direct line to the build team',
];

const FAQ = [
  {
    q: 'Why is there no price on this page?',
    a: 'Because one number was doing a bad job for everyone. It looked high to a two-person cheesemaker and said nothing useful to a multi-site manufacturer. Keystone is configured to the operation in front of us, and the quote follows the configuration. We would rather spend twenty minutes understanding your business than publish a figure that puts off the people it would help most.',
  },
  {
    q: 'Does no published price mean it is expensive?',
    a: 'No. It means it is sized. Small producers are explicitly part of who Keystone is for. Tell us on the call that you are a two or three person operation and we will tell you honestly whether Keystone fits, what a sensible starting scope looks like, and what it would cost, in writing.',
  },
  {
    q: 'What is the catch?',
    a: 'Implementation is billed once, separately, and quoted before you commit. Customisations and integrations after go-live are quoted separately too. We do not hide setup cost in the recurring fee, which is how vendors lock you in for two years to recover their margin. Licence is what it costs to run, implementation is what it costs to fit.',
  },
  {
    q: 'Is there a free trial?',
    a: 'No. We run a walkthrough on a real production-data sandbox, then write you a fixed-fee scope. If we agree, we build. If we do not, you keep the scope document and walk away. Free trials of production software are a way to look busy, not a way to make a good decision.',
  },
  {
    q: 'How long does implementation take?',
    a: 'Typically 4 to 6 weeks for a standard single-site food producer. 6 to 10 weeks if you have multiple retailer EDI integrations or a complex accounts-package sync. If a vendor needs six months, they are building you, not configuring software.',
  },
  {
    q: 'Where is the data hosted?',
    a: 'AWS Ireland (eu-west-1, Dublin) by default. Each tenant runs on its own dedicated server with its own database, no shared infrastructure. Encrypted at rest, TLS 1.3 in transit, KMS-managed keys. Daily encrypted backups, point-in-time restore. Or we can deploy inside your own AWS account.',
  },
  {
    q: 'What if we want to leave?',
    a: 'You get all your data exported as database dumps and CSVs within 48 hours. No lock-in, no penalty clauses, no data egress fees. We keep customers by being good, not by holding data hostage.',
  },
];

const Eyebrow = ({ children, color = PALETTE.magenta }) => (
  <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color, marginBottom: 14 }}>{children}</div>
);

const DemoCta = ({ testid, dark }) => (
  <Link
    to="/site/contact"
    data-testid={testid}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      padding: '16px 30px',
      background: dark ? PALETTE.gold : PALETTE.navy,
      color: dark ? PALETTE.navy : '#fff',
      textDecoration: 'none', fontSize: 13.5, fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 6,
    }}
  >
    Request a personalised demonstration <span>→</span>
  </Link>
);

export default function SitePricing() {
  return (
    <SiteLayout>
      <SEO
        title="Keystone Pricing · Built around your business, quoted to your operation"
        description="Production software for food manufacturers, configured around the way you work. Sized to your operation, from small family cheesemakers to established manufacturers. Licence and fixed-fee implementation quoted in writing after one call."
        path="/site/pricing"
        keywords="food ERP pricing, dairy software cost, food manufacturing software pricing, cheesemaker software pricing, food traceability software cost, small food producer software"
      />

      {/* HERO */}
      <section data-testid="site-pricing-hero" style={{ background: PALETTE.cream, padding: '160px 32px 72px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Eyebrow>Pricing</Eyebrow>
          <h1 style={{ fontFamily: SERIF, fontSize: 'clamp(42px, 6vw, 76px)', fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0, maxWidth: 900 }}>
            Built around your business,<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>not the other way around.</em>
          </h1>
          <p style={{ fontSize: 21, lineHeight: 1.55, color: PALETTE.slate, marginTop: 28, maxWidth: 720 }}>
            Whether you are a small family cheesemaker or an established food manufacturer, Keystone is configured around the way you work. <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>Start with the capabilities you need today and expand as your production grows.</strong>
          </p>
          <div style={{ marginTop: 36, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <DemoCta testid="pricing-hero-demo-cta" />
            <a href="tel:+353851810310" data-testid="pricing-hero-phone" style={{ color: PALETTE.navy, fontWeight: 600, fontSize: 15, textDecoration: 'underline', textUnderlineOffset: 6 }}>
              or call +353 85 181 0310
            </a>
          </div>
        </div>
      </section>

      {/* HOW WE PRICE */}
      <section data-testid="pricing-principles" style={{ background: PALETTE.paper, padding: '80px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.6fr', gap: 56, alignItems: 'start' }} className="pr-row">
            <div>
              <Eyebrow>How we price</Eyebrow>
              <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(30px, 4vw, 44px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
                One product.<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Sized to the operation.</em>
              </h2>
              <p style={{ fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 18 }}>
                A single licence should not cost a two-person creamery what it costs a sixty-person factory. So we do not publish one number and ask everyone to fit it.
              </p>
            </div>
            <div>
              {PRINCIPLES.map((p, i) => (
                <div key={p.t} style={{ display: 'flex', gap: 22, padding: '22px 0', borderTop: `1px solid ${PALETTE.border}` }}>
                  <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', background: PALETTE.navy, color: PALETTE.gold, fontFamily: SERIF, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 700, color: PALETTE.navy, margin: '0 0 8px', letterSpacing: '-0.01em' }}>{p.t}</h3>
                    <p style={{ fontSize: 15, lineHeight: 1.65, color: PALETTE.slate, margin: 0 }}>{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .pr-row { grid-template-columns: 1fr !important; gap: 28px !important; } }`}</style>
      </section>

      {/* WHAT SHAPES THE QUOTE + SMALL PRODUCERS */}
      <section style={{ background: PALETTE.cream, padding: '80px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 36, alignItems: 'stretch' }} className="quote-row">
          <div data-testid="pricing-quote-inputs" style={{ background: PALETTE.navy, color: '#fff', borderRadius: 8, padding: '44px 40px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${PALETTE.magenta} 0%, ${PALETTE.gold} 100%)` }} />
            <Eyebrow color={PALETTE.gold}>What shapes your quote</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 700, margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
              Operational complexity, <em style={{ fontStyle: 'italic', color: PALETTE.gold, fontWeight: 500 }}>not seat count.</em>
            </h2>
            <ul style={{ padding: 0, margin: '26px 0 0', listStyle: 'none' }}>
              {QUOTE_INPUTS.map(item => (
                <li key={item} style={{ display: 'flex', gap: 14, padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 14.5, lineHeight: 1.55, color: '#E5E7EB' }}>
                  <span style={{ flexShrink: 0, marginTop: 4, width: 16, height: 16, borderRadius: '50%', background: PALETTE.gold, color: PALETTE.navy, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.6, marginTop: 22, marginBottom: 0 }}>
              Unlimited reasonable-use users within the licensed scope. Bulk bot accounts, API scraping at line rates and shared third-party logins are not reasonable use.
            </p>
          </div>

          <div data-testid="pricing-small-producers" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.magenta}`, borderRadius: 8, padding: '44px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <Eyebrow>For smaller producers</Eyebrow>
              <h2 style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 700, color: PALETTE.navy, margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
                No published price does not mean <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>out of reach.</em>
              </h2>
              <p style={{ fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 18 }}>
                Many of the producers we talk to are two or three people making exceptional food with a shed full of spreadsheets. Keystone was shaped on a working cheese floor, and it scales down as well as up.
              </p>
              <p style={{ fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate, marginTop: 12 }}>
                Start with the batch record, the stock and the traceability you need for the next audit. Add dispatch, retailer labels and integrations when the business asks for them, not before.
              </p>
            </div>
            <div style={{ marginTop: 28, paddingTop: 22, borderTop: `1px solid ${PALETTE.border}` }}>
              <CashelBlueCompactQuote />
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .quote-row { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* LICENCE COVERS / SCOPED SEPARATELY */}
      <section style={{ background: PALETTE.paper, padding: '80px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }} className="lic-row">
          <div data-testid="pricing-licence-covers">
            <Eyebrow>What the licence covers</Eyebrow>
            <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
              {LICENCE_INCLUDES.map(item => (
                <li key={item} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: `1px solid ${PALETTE.border}`, fontSize: 14.5, lineHeight: 1.55, color: PALETTE.ink }}>
                  <span style={{ flexShrink: 0, marginTop: 5, width: 7, height: 7, borderRadius: '50%', background: PALETTE.magenta }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: PALETTE.muted, lineHeight: 1.55, marginTop: 18 }}>
              No existing feature you use will be moved behind a paywall during your contract term. If a future module is separately licensed, you get 90 days' notice.
            </p>
          </div>
          <div data-testid="pricing-scoped-separately">
            <Eyebrow color={PALETTE.muted}>Scoped and quoted separately</Eyebrow>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: PALETTE.slate, margin: '0 0 12px' }}>
              Real engineering effort that depends on your operation, so it is quoted on its own and never buried in the licence.
            </p>
            <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
              {SCOPED_SEPARATELY.map(item => (
                <li key={item} style={{ display: 'flex', gap: 14, padding: '10px 0', borderBottom: `1px solid ${PALETTE.border}`, fontSize: 14, lineHeight: 1.5, color: PALETTE.slate }}>
                  <span style={{ flexShrink: 0, marginTop: 6, width: 14, height: 14, borderRadius: '50%', border: `1px solid ${PALETTE.muted}` }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: PALETTE.muted, lineHeight: 1.55, marginTop: 18, fontStyle: 'italic' }}>
              Hardware (label printers, scanners, scales) sourced separately. We spec it, you buy direct.
            </p>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .lic-row { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
      </section>

      {/* HOSTING STRIP */}
      <section style={{ background: PALETTE.cream, padding: '48px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 32px', background: '#fff', border: `1px solid ${PALETTE.border}`, borderRadius: 6, display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 28, alignItems: 'center' }} className="trust-strip">
          <div>
            <Eyebrow>Always included</Eyebrow>
            <div style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 700, color: PALETTE.navy, lineHeight: 1.25, letterSpacing: '-0.01em', marginTop: -8 }}>
              Dedicated tenant server. <em style={{ fontStyle: 'italic', color: PALETTE.magenta, fontWeight: 500 }}>Full GDPR.</em>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 4 }}>Hosting</div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: PALETTE.slate }}>Managed AWS EU (eu-west-1, Dublin), or deploy in your own AWS account.</div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 4 }}>Standards</div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: PALETTE.slate }}>GDPR · Tamper-evident audit trail · Documented traceability and audit-evidence workflows</div>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .trust-strip { grid-template-columns: 1fr !important; gap: 18px !important; padding: 22px 22px !important; } }`}</style>
      </section>

      {/* IMPLEMENTATION */}
      <section style={{ background: PALETTE.paper, padding: '90px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64, alignItems: 'start' }} className="impl-row">
          <div>
            <Eyebrow>Implementation, billed once</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
              Make it fit<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>your operation.</em>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, marginTop: 18 }}>
              A separate, fixed-fee project to tailor Keystone to how your factory actually runs. Quoted in writing before you commit, no overruns. Once it is built and handed over, you move to the recurring licence and we stop charging for setup.
            </p>
            <div style={{ marginTop: 28, padding: 24, background: PALETTE.bgSoft, border: `1px solid ${PALETTE.border}`, borderLeft: `3px solid ${PALETTE.gold}`, borderRadius: 4 }}>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 8 }}>How it is sized</div>
              <p style={{ fontSize: 14, color: PALETTE.slate, lineHeight: 1.6, margin: 0 }}>
                A small single-site producer with one accounts package and a handful of customers is a short, light project. Multiple retailer EDI feeds, years of historical data or several sites push the scope up. Either way the number is fixed before you sign.
              </p>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 14 }}>What is in the implementation block</div>
            <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
              {IMPL_INCLUDES.map((item, i) => (
                <li key={item} style={{ display: 'flex', gap: 18, padding: '14px 0', borderBottom: `1px solid ${PALETTE.border}`, fontSize: 14.5, lineHeight: 1.55, color: PALETTE.ink }}>
                  <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: PALETTE.navy, color: PALETTE.gold, fontFamily: SERIF, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .impl-row { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
      </section>

      <CostOfInactionCalculator />
      <RoiCalculator />

      {/* PILOT */}
      <section style={{ background: PALETTE.paper, padding: '90px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="pilot-row">
          <div>
            <Eyebrow>Better than a free trial</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
              The Pilot Programme.<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>30 days on your real data.</em>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.65, color: PALETTE.slate, marginTop: 18 }}>
              Free trials of production software are theatre. You cannot try traceability without your customer codes mapped, your dispatch templates configured, your products imported. So we do not pretend.
            </p>
            <p style={{ fontSize: 16, lineHeight: 1.65, color: PALETTE.slate, marginTop: 14 }}>
              Instead: a real, scaled-down implementation for thirty days. Your data, our screens, every dispatch through Keystone. The pilot is a fixed fee agreed up front. If you continue, it credits in full against implementation. If you walk away, <strong style={{ color: PALETTE.navy }}>half of it is refundable</strong>, your data exported as CSVs, no awkwardness.
            </p>
          </div>
          <div data-testid="pricing-pilot-card" style={{ padding: 36, background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, borderTop: `4px solid ${PALETTE.magenta}`, borderRadius: 6 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.muted, marginBottom: 8 }}>Pilot programme · fixed fee, agreed up front</div>
            <ul style={{ padding: 0, margin: '12px 0 0', listStyle: 'none', borderTop: `1px solid ${PALETTE.border}` }}>
              {[
                'Live tenant on managed AWS EU (Dublin)',
                'Discovery call + data import setup',
                'Customer codes and dispatch templates configured',
                'Full feature access for your team',
                '30 days of real production usage',
                'CSV data export at any time, no penalty',
                'Pilot fee credits in full to implementation if you continue',
                '50% refundable if you do not proceed within 14 days of pilot end',
              ].map(b => (
                <li key={b} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${PALETTE.border}`, fontSize: 13.5, lineHeight: 1.55, color: PALETTE.ink }}>
                  <span style={{ flexShrink: 0, marginTop: 5, width: 5, height: 5, borderRadius: '50%', background: PALETTE.magenta }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <style>{`@media (max-width: 900px) { .pilot-row { grid-template-columns: 1fr !important; gap: 32px !important; } }`}</style>
      </section>

      {/* FAQ */}
      <section style={{ background: PALETTE.cream, padding: '100px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <Eyebrow>Questions worth answering</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(30px, 4vw, 44px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>Things people actually ask.</h2>
          </div>
          {FAQ.map((f, i) => (
            <details key={i} data-testid={`pricing-faq-${i}`} style={{ borderTop: `1px solid ${PALETTE.border}`, padding: '24px 0' }}>
              <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, fontFamily: SERIF, fontSize: 22, fontWeight: 600, color: PALETTE.navy, letterSpacing: '-0.01em' }}>
                {f.q}
                <span style={{ color: PALETTE.magenta, fontSize: 24, fontWeight: 300 }}>+</span>
              </summary>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 16, marginBottom: 0 }}>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: PALETTE.navy, color: '#fff', padding: '100px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(32px, 4.4vw, 48px)', fontWeight: 700, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
            Pricing is the easy part.<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Fit is the hard part.</em>
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: '#CBD5E1', marginTop: 22, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto' }}>
            Twenty minutes with the team that builds Keystone. We tell you whether it is right for your operation, what a sensible starting scope looks like, and what it would cost, in writing.
          </p>
          <div style={{ marginTop: 32 }}>
            <DemoCta testid="pricing-cta" dark />
          </div>
          <div style={{ marginTop: 18, fontSize: 14, color: '#94A3B8' }}>
            Direct line: <a href="tel:+353851810310" style={{ color: PALETTE.gold, fontWeight: 700, textDecoration: 'none' }}>+353 85 181 0310</a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
